import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { type MessageDescriptor, useIntl } from 'react-intl';

import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import { surveyTotals } from '../../jsm-portal-playground-hr-data';
import { jsmPortalPlaygroundHrMessages as messages } from '../../jsm-portal-playground-hr-messages';

type StatTile = {
	id: string;
	Icon: typeof EyeOpenIcon;
	value: MessageDescriptor;
	values: Record<string, number>;
	tone: 'purple' | 'gray' | 'blue';
	iconColor: ComponentProps<typeof EyeOpenIcon>['color'];
};

const tiles: StatTile[] = [
	{
		id: 'views',
		Icon: EyeOpenIcon,
		value: messages.statViews,
		values: { count: surveyTotals.views },
		tone: 'purple',
		iconColor: token('color.icon.accent.purple'),
	},
	{
		id: 'responses',
		Icon: ChartBarIcon,
		value: messages.statResponses,
		values: { count: surveyTotals.responses },
		tone: 'gray',
		iconColor: token('color.icon.accent.green'),
	},
	{
		id: 'completion',
		Icon: PeopleGroupIcon,
		value: messages.statCompletion,
		values: { rate: surveyTotals.completionRate },
		tone: 'blue',
		iconColor: token('color.icon.accent.blue'),
	},
];

/** The three "Stat callouts" above the question summaries. */
export const SurveyStatTiles = () => {
	const intl = useIntl();

	return (
		<ul css={rowStyles}>
			{tiles.map(({ id, Icon, value, values, tone, iconColor }) => (
				<li key={id} css={statTileStyles}>
					<span
						css={[
							iconTileStyles,
							tone === 'purple' && purpleTileStyles,
							tone === 'gray' && grayTileStyles,
							tone === 'blue' && blueTileStyles,
						]}
					>
						<Icon color={iconColor} label="" />
					</span>
					<div css={valueStackStyles}>
						<span css={valueStyles}>{intl.formatMessage(value, values)}</span>
						<Text size="small" color="color.text.subtle">
							{intl.formatMessage(messages.statSince)}
						</Text>
					</div>
				</li>
			))}
		</ul>
	);
};

const rowStyles = css({
	display: 'flex',
	gap: token('space.200'),
	width: '100%',
	margin: 0,
	padding: 0,
	listStyle: 'none',
});

const statTileStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexBasis: 0,
	flexGrow: 1,
	gap: token('space.100'),
	boxSizing: 'border-box',
	minWidth: 0,
	margin: 0,
	paddingBlock: token('space.150'),
	paddingInlineStart: token('space.100'),
	paddingInlineEnd: token('space.150'),
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface.raised'),
});

const iconTileStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: '40px',
	height: '40px',
	borderRadius: token('radius.tile'),
});

const purpleTileStyles = css({
	backgroundColor: token('color.background.accent.purple.subtlest'),
});

const grayTileStyles = css({
	backgroundColor: token('color.background.accent.gray.subtlest'),
});

const blueTileStyles = css({
	backgroundColor: token('color.background.accent.blue.subtlest'),
});

const valueStackStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	minWidth: 0,
});

const valueStyles = css({
	color: token('color.text'),
	font: token('font.heading.small'),
});
